"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Search, X, ChevronDown, Check, Music } from "lucide-react";
import { navLinks } from "@/constants/navlinks";
import { getPageTheme } from "@/constants/pageThemes";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AlexLogo from "../icons/AlexLogo";
import Hamburger from "../icons/Hamburger";
import CustomButton from "./CustomButton";
import SearchModal from "./SearchModal";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useLongPress } from "@/hooks/useLongPress";
import { trackBookAppointmentClicked } from "@/lib/analytics";
import NavbarRain from "../easter-eggs/NavbarRain";
import { BREATHING_EXERCISE_EVENT } from "../easter-eggs/BreathingExercise";
import CallExpandBtn from "./CallExpandBtn";

const Navbar = () => {
  const pathname = usePathname();
  const theme = getPageTheme(pathname);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMoonOpen, setIsMoonOpen] = useState(false);
  const moonRef = useRef<HTMLDivElement>(null);
  const moonMobileRef = useRef<HTMLDivElement>(null);
  const { tracks, currentTrack, isPlaying, play, stop } = useAudioPlayer();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const inDesktop = moonRef.current?.contains(e.target as Node);
      const inMobile = moonMobileRef.current?.contains(e.target as Node);
      if (!inDesktop && !inMobile) setIsMoonOpen(false);
    }
    if (isMoonOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMoonOpen]);

  const openDropdown = (href: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(href);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const handleTrackSelect = (track: (typeof tracks)[0]) => {
    play(track);
    setIsMoonOpen(false);
  };

  const handleMusicIconClick = () => {
    if (isPlaying) {
      stop();
      setIsMoonOpen(false);
    } else {
      setIsMoonOpen((o) => !o);
    }
  };

  // Mobile fallback for the Konami-code breathing exercise (long-press the logo).
  const logoLongPress = useLongPress(() => {
    window.dispatchEvent(new Event(BREATHING_EXERCISE_EVENT));
  });

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/50 bg-black/50 backdrop-blur-[50px]">
      <NavbarRain active={isPlaying} />
      <div className="flex items-center justify-between px-10 py-3">
        {/* Logo */}
        <Link href="/" className="shrink-0" {...logoLongPress}>
          <AlexLogo />
        </Link>

        {/* Desktop: pill nav */}
        <div className="hidden items-center gap-7 xl:flex">
          <nav className="flex items-center gap-2 rounded-[18px] p-2">
            {navLinks.map((item) => {
              const isExactOnly = item.href === "/";
              const isActive = isExactOnly
                ? pathname === item.href
                : pathname === item.href ||
                  pathname.startsWith(item.href + "/");
              const hasDropdown = item.dropdown && item.dropdown.length > 0;
              const isOpen = activeDropdown === item.href;

              if (!hasDropdown) {
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`rounded-xl px-2.5 py-2.5 font-onest text-sm font-normal leading-none transition-all duration-200 ${
                      isActive
                        ? "bg-white text-black"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => openDropdown(item.href)}
                  onMouseLeave={scheduleClose}
                >
                  <Link
                    href={item.href}
                    className={`flex items-center gap-1 rounded-xl px-2.5 py-2.5 font-onest text-sm font-normal leading-none transition-all duration-200 ${
                      isActive
                        ? "bg-white text-black"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      strokeWidth={2}
                      className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </Link>

                  {isOpen && (
                    <div
                      onMouseEnter={() => openDropdown(item.href)}
                      onMouseLeave={scheduleClose}
                      className="absolute left-1/2 top-full z-50 min-w-[180px] -translate-x-1/2 pt-3"
                    >
                      <div className="flex flex-col rounded-2xl border border-white/20 bg-black/80 p-1.5 shadow-xl backdrop-blur-[50px]">
                        {item.dropdown!.map((sub) => (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            onClick={() => setActiveDropdown(null)}
                            className="rounded-xl px-3 py-2 font-onest text-sm font-normal text-white/80 transition-all duration-150 hover:bg-white/10 hover:text-white"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Desktop: icons + CTA */}
        <div className="hidden items-center gap-4 xl:flex">
          {/* Moon / music picker */}
          <div ref={moonRef} className="relative">
            <button
              onClick={handleMusicIconClick}
              aria-label={
                isPlaying ? "Stop ambient music" : "Play ambient music"
              }
              className={`flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-white transition-colors hover:bg-white/10 ${
                isPlaying ? "text-primary-200" : ""
              }`}
            >
              <Music
                size={22}
                strokeWidth={1.5}
                className={isPlaying ? "fill-primary-200 text-white" : ""}
              />
            </button>

            {isMoonOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[180px] overflow-hidden rounded-xl border border-white/15 bg-black/80 py-1.5 shadow-2xl backdrop-blur-xl">
                <p className="px-3 pb-1.5 pt-1 font-onest text-[11px] font-medium uppercase tracking-wider text-white/40">
                  Ambient Music
                </p>
                {tracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => handleTrackSelect(track)}
                    className="flex w-full items-center gap-2 px-3 py-2 font-onest text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <span className="flex-1 text-left">{track.label}</span>
                    {currentTrack?.id === track.id && (
                      <Check size={13} className="shrink-0 text-primary-200" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <button
            onClick={() => setIsSearchOpen(true)}
            aria-label="Search"
            className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-white transition-colors hover:bg-white/10"
          >
            <Search size={22} strokeWidth={1.5} />
          </button>

          <CallExpandBtn variant="light" size="sm" />
          {/* "Book an Appointment" CTA */}
          <CustomButton
            label="Book an Appointment"
            href="/booking?reset=1"
            variant="primary"
            size="sm"
            theme={theme}
            withIcon
            onClick={() =>
              trackBookAppointmentClicked({ location: "navbar_desktop" })
            }
          />
        </div>

        {/* Mobile: moon + sheet drawer + search */}
        <div className="flex items-center gap-2 xl:hidden">
          {/* Moon button (mobile) */}
          <div ref={moonMobileRef} className="relative">
            <button
              onClick={handleMusicIconClick}
              aria-label={
                isPlaying ? "Stop ambient music" : "Play ambient music"
              }
              className={`flex h-9 w-9 items-center justify-center rounded-[10px] text-white transition-colors hover:bg-white/10 ${
                isPlaying ? "text-primary-200" : ""
              }`}
            >
              <Music
                size={24}
                strokeWidth={1.5}
                className={isPlaying ? "fill-primary-200 text-white" : ""}
              />
            </button>

            {isMoonOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[180px] overflow-hidden rounded-xl border border-white/15 bg-black/90 py-1.5 shadow-2xl backdrop-blur-xl">
                <p className="px-3 pb-1.5 pt-1 font-onest text-[11px] font-medium uppercase tracking-wider text-white/40">
                  Ambient Music
                </p>
                {tracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => handleTrackSelect(track)}
                    className="flex w-full items-center gap-2 px-3 py-2 font-onest text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <span className="flex-1 text-left">{track.label}</span>
                    {currentTrack?.id === track.id && (
                      <Check size={13} className="shrink-0 text-primary-200" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <button
            onClick={() => setIsSearchOpen(true)}
            aria-label="Search"
            className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] text-white transition-colors hover:bg-white/10"
          >
            <Search size={22} strokeWidth={1.5} />
          </button>

          {/* Mobile sheet drawer */}
          <Sheet>
            <SheetTrigger asChild className="pr-2">
              <button aria-label="Open menu">
                <Hamburger />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-auto max-w-none flex-col border-0 p-0 sm:max-w-[489px] [&>button]:hidden"
              style={{
                background:
                  "radial-gradient(272.69% 140.7% at 1.64% -0.6%, #FFF 55.64%, #B7D6FF 100%)",
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4">
                <AlexLogo
                  className="h-[24px] w-auto"
                  style={{ filter: "invert(1)" }}
                />
                <SheetClose asChild>
                  <button
                    aria-label="Close menu"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-black/50 transition-colors hover:bg-black/5 hover:text-black"
                  >
                    <X size={20} />
                  </button>
                </SheetClose>
              </div>

              {/* Nav - All Pages */}
              <Accordion type="single" collapsible className="flex flex-col">
                {navLinks.map((item, i) =>
                  item.dropdown ? (
                    <AccordionItem
                      key={i}
                      value={`mobile-${i}`}
                      className="border-none"
                    >
                      <AccordionTrigger className="px-5 py-2.5 font-crimsonText text-[18px] font-semibold leading-none text-black hover:no-underline [&>svg]:ml-20 [&>svg]:box-content [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-black/40">
                        <SheetClose asChild>
                          <Link
                            href={item.href}
                            className="flex-1 text-left transition-colors hover:text-[#006DFF]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      </AccordionTrigger>
                      <AccordionContent className="ml-5 flex flex-col border-l border-[rgba(0,109,255,0.2)] pb-0">
                        {item.dropdown.map((sub, j) => (
                          <SheetClose asChild key={j}>
                            <Link
                              href={sub.href}
                              className="block px-3 py-1.5 font-onest text-[14px] font-normal text-black/50 transition-colors hover:text-black"
                            >
                              {sub.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ) : (
                    <div key={i} className="border-none">
                      <SheetClose asChild>
                        <Link
                          href={item.href}
                          className="block px-5 py-2.5 font-crimsonText text-[18px] font-semibold leading-none text-black transition-colors hover:text-[#006DFF]"
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    </div>
                  ),
                )}
              </Accordion>

              {/* CTA */}
              <div className="mt-auto flex flex-col gap-3 px-5 pb-6 pt-4">
                <SheetClose asChild>
                  <CallExpandBtn variant="dark" theme={theme} size="sm" />
                </SheetClose>
                <SheetClose asChild>
                  <CustomButton
                    label="Book an Appointment"
                    href="/booking?reset=1"
                    variant="primary"
                    withIcon
                    size="lg"
                    theme={theme}
                    onClick={() =>
                      trackBookAppointmentClicked({
                        location: "navbar_mobile_drawer",
                      })
                    }
                  />
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>

          {/* CTA — visible from md up */}
          <div className="hidden md:flex">
            <CallExpandBtn variant="light" size="sm" />
            <CustomButton
              label="Book an Appointment"
              href="/booking?reset=1"
              variant="primary"
              size="sm"
              theme={theme}
              withIcon
              onClick={() =>
                trackBookAppointmentClicked({
                  location: "navbar_mobile_inline",
                })
              }
            />
          </div>
        </div>
      </div>

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </header>
  );
};

export default Navbar;
